import { useTranslation } from "react-i18next";
import { Bot, UserCheck, type LucideIcon } from "lucide-react";

import { formatCount } from "@/lib/format";
import { cn } from "@/lib/utils";
import { ACTIVATE_PHASES } from "../acts";
import { Section, type DiagnosticTabProps } from "../DiagnosticSection";
import { clientChartAccounts, migrationScope } from "../diagnosticData";

/**
 * Ato 4 · O tratamento — "Como conduzimos daqui." (#852)
 *
 * O ato que ancora o número do Ato 5 em confiança. Ele mostra **processo e
 * governança**, e a nota do apresentador proíbe mostrar a engenharia da
 * plataforma.
 *
 * Dois blocos, e a diferença entre eles é de natureza:
 *
 * - a linha do tempo das fases é **copy de metodologia**, não dado, e o card diz
 *   isso em vez de fingir procedência. O desenho mostra a duração de cada fase
 *   ("Prepare 1,0 · Explore 2,5"), e esses números não têm campo no payload;
 *
 * "O que migra de dados (SAP Migration Cockpit)" entrou em 24/09. O desenho o assina por `data_migration_scope`, que não existe; quem
 * responde a pergunta é `data_volumes`, que já vinha no payload sem leitor.
 *
 * O par "Os agentes fazem" e "Sua equipe decide",
 * entre a linha do tempo e o que migra, como no desenho. É copy de metodologia,
 * no mesmo caso da linha do tempo.
 *
 * **O card "Rastreabilidade: quem decidiu o quê" saiu em 24/09/2026**, na
 * reunião de validação com o time comercial. A decisão foi do Marco, com o
 * Guilherme de acordo: o log de aprovação de scope item é interno e não diz
 * nada ao cliente, que quer saber o que vai receber. O que o time pediu no
 * lugar é outra coisa e de outro momento — um registro do consultor ("conversei
 * com o cliente e ele decidiu X"), na condução do projeto e não no painel de
 * venda. A seção `scope_decisions` continua no payload.
 *
 * Este arquivo era a aba Rastreabilidade. O card `user_sku_sample` saiu inteiro:
 * a nota do Ato 1 proíbe lista nominal de usuário por escrito, e "Rastreabilidade"
 * no desenho é o título de outro card, sobre `scope_decisions`. O produtor
 * continua, porque é o insumo dos números de licença do Ato 5.
 */
export function TreatmentAct({
  sections,
  snapshots,
  validation,
}: DiagnosticTabProps) {
  return (
    <div className="grid grid-cols-1 gap-4">
      <ActivatePhases />
      <RoleSplit />
      {/* A jornada e a divisão de papéis são narrativa e pedem largura cheia;
          os dois cards de dado emparelham, como no desenho. */}
      <DataMigrationCard
        sections={sections}
        snapshots={snapshots}
        validation={validation}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* o que migra de dados                                                        */
/* -------------------------------------------------------------------------- */

type T = (key: string, opts?: Record<string, unknown>) => string;

/** Rótulo de cada linha que migra. */
const MIGRATION_LABELS = (t: T): Record<string, string> => ({
  materials: t("Materials"),
  business_partners: t("Business partners"),
  gl_accounts: t("G/L accounts"),
  cost_centers: t("Cost centers"),
});

/**
 * O que migra de dados (SAP Migration Cockpit) — o card do desenho do Ato 4.
 *
 * Lê `data_volumes`, que já existia no payload e nenhum ato lia: ela perdeu o
 * card na #852 porque o Ato 1 pede volumetria por processo e por mês, que é
 * outra medição. A pergunta deste ato é a que as dez tabelas respondem.
 *
 * A mensagem do card é o subtítulo do desenho, "sem migração de histórico
 * transacional", e ela precisa de dois números lado a lado para se sustentar:
 * o que atravessa e o que fica. Medido no corpus da demo, 62.018 contra
 * 783.143 — 7% do volume migra.
 *
 * As duas linhas de documento em aberto que o desenho pede ("pedidos de venda
 * em aberto" e "ordens de compra em aberto") saem do recorte que a #880 passou
 * a coletar, e ele só existe nas execuções novas. Execução anterior não tem o
 * bloco, e o card declara a ausência em vez de mostrar o total da tabela, que
 * superestimaria o que migra.
 */
function DataMigrationCard({
  sections,
  snapshots,
  validation,
}: DiagnosticTabProps) {
  const { t } = useTranslation();
  const scope = migrationScope(sections, {
    glAccounts: clientChartAccounts(validation),
  });
  const labels = MIGRATION_LABELS(t);
  const peak =
    scope?.master.reduce((acc, line) => Math.max(acc, line.count), 0) ?? 0;

  return (
    <Section
      sections={sections}
      name="data_volumes"
      title={t("What migrates of data (SAP Migration Cockpit)")}
      snapshots={snapshots}
      showsSampling={false}
    >
      {scope ? (
        <div data-testid="data-migration">
          <p className="mb-3 text-[11px] text-[#596b85]">
            {t(
              "Standard cockpit objects only, load validated by your team, with no transactional history migrated",
            )}
          </p>

          <ul className="space-y-2">
            {scope.master.map((line) => (
              <li
                key={line.key}
                data-testid={`migration-row-${line.key}`}
                className="flex items-center gap-3 text-xs"
              >
                <span className="w-40 shrink-0 truncate text-[#08254c]">
                  {labels[line.key] ?? line.key}
                </span>
                {/* Barra simples, e não `StackedBar`: aqui a barra é uma só,
                    comparada contra a maior linha. No empilhado o espaço vazio
                    à direita teria de ser um segmento de verdade, e o
                    primitivo compõe `aria-label` e `title` de todo segmento —
                    o leitor de tela anunciava o enchimento como se fosse
                    medição. */}
                <div className="h-3 flex-1 overflow-hidden rounded-sm bg-[#eef1f5]">
                  <div
                    className="h-full bg-[#4a7fd4]"
                    style={{
                      width: peak > 0 ? `${(line.count / peak) * 100}%` : "0%",
                    }}
                  />
                </div>
                <span className="w-16 shrink-0 text-right tabular-nums text-[#08254c]">
                  {formatCount(line.count)}
                </span>
              </li>
            ))}
          </ul>

          <div className="mt-4 border-t border-[#e8ebf0] pt-3 text-[11px] leading-relaxed text-[#596b85]">
            <p data-testid="migration-summary">
              {t(
                "{{master}} master records migrate. The {{legacy}} documents of history stay in the legacy system.",
                {
                  master: formatCount(scope.masterTotal),
                  legacy: formatCount(scope.legacyTotal),
                },
              )}
            </p>
            {scope.isFloor ? (
              <p className="italic" data-testid="migration-floor">
                {t("At least: one of the counted tables hit the read cap.")}
              </p>
            ) : null}
            {/* As duas linhas do desenho. Execução anterior à #880 não as
                coletou, e aí o card declara a ausência em vez de mostrar o
                total da tabela, que superestimaria o que migra. */}
            {scope.open ? (
              <div className="mt-1" data-testid="migration-open">
                {scope.open.sales !== undefined ? (
                  <p>
                    {t("Open sales orders: {{count}}", {
                      count: formatCount(scope.open.sales),
                    })}
                  </p>
                ) : null}
                {scope.open.purchases !== undefined ? (
                  <p>
                    {t("Open purchase orders: {{count}}", {
                      count: formatCount(scope.open.purchases),
                    })}
                  </p>
                ) : null}
                {scope.open.isFloor ? (
                  <p className="italic">
                    {t(
                      "One of these tables hit the read cap, so the count is a floor.",
                    )}
                  </p>
                ) : null}
              </div>
            ) : (
              <p className="mt-1" data-testid="migration-open-caveat">
                {t(
                  "Open sales and purchase documents also migrate; this run did not measure them.",
                )}
              </p>
            )}
          </div>
        </div>
      ) : null}
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/* as cinco fases da metodologia                                               */
/* -------------------------------------------------------------------------- */

/**
 * Sem `Section` de propósito: não há envelope, porque não há seção.
 *
 * Um `DiagnosticSection` aqui prometeria procedência e estado que este bloco não
 * tem, e o rodapé sairia vazio ou mentindo. Ele declara em texto o que é.
 */
function ActivatePhases() {
  const { t } = useTranslation();

  return (
    <section
      data-testid="diagnostic-activate-phases"
      className="rounded-lg border border-[#e8ebf0] bg-white p-6"
    >
      <h3 className="font-brand text-[15px] font-bold text-[#08254c]">
        {t("How the journey runs")}
      </h3>

      <ol className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3 xl:grid-cols-5">
        {ACTIVATE_PHASES.map((phase, i) => (
          <li
            key={phase.key}
            className="flex flex-col gap-1.5 border-t-2 border-[#d4dae3] pt-3 first:border-[#08254c]"
          >
            <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-[#7b8799]">
              {String(i + 1).padStart(2, "0")} · {t(phase.phase)}
            </span>
            <span className="text-[14px] font-semibold text-[#08254c]">
              {t(phase.title)}
            </span>
            <span className="text-[12.5px] leading-snug text-[#596b85]">
              {t(phase.body)}
            </span>
          </li>
        ))}
      </ol>

      <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.08em] text-[#7b8799]">
        {t(
          "Exed methodology. Not measured data: phase duration has no producer yet.",
        )}
      </p>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* quem faz e quem decide                                                      */
/* -------------------------------------------------------------------------- */

/** O que cabe aos agentes, na ordem do desenho. */
const AGENT_TASKS = (t: T): string[] => [
  t("Continuous extraction and analysis of the source landscape"),
  t("Proposed scope item activation, with a rationale backed by the data"),
  t("Migration of eligible WRICEF code to Clean Core"),
  t("Automated tests with audit-ready evidence"),
];

/**
 * O que cabe à equipe do cliente, na ordem do desenho.
 *
 * O desenho diz "cada um dos 43 gaps", e o número ficou de fora: este card é
 * copy de metodologia e não tem produtor. Puxar um agregado de gaps do payload
 * daria procedência a um texto que não a tem.
 */
const TEAM_DECISIONS = (t: T): string[] => [
  t("Approves or rejects each recommendation in Fit-to-Standard"),
  t("Chooses the path for each gap"),
  t("Validates the migrated data and the test scenarios"),
  t("Keeps control of what goes into production"),
];

/**
 * "Os agentes fazem" e "Sua equipe decide", lado a lado (#923).
 *
 * Mesmo caso da linha do tempo: nenhum dos dois lados tem campo no payload.
 * Por isso não é `Section` e não tem `SourceFooter`; a linha de baixo, no lugar
 * da origem, diz o que o card é.
 */
function RoleSplit() {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <RoleCard
        testId="diagnostic-agents-do"
        title={t("The agents do")}
        icon={Bot}
        tone="text-[#6d8fc4]"
        items={AGENT_TASKS(t)}
      />
      <RoleCard
        testId="diagnostic-team-decides"
        title={t("Your team decides")}
        icon={UserCheck}
        tone="text-[#27a155]"
        items={TEAM_DECISIONS(t)}
      />
    </div>
  );
}

interface RoleCardProps {
  testId: string;
  title: string;
  icon: LucideIcon;
  /** Cor do rótulo e dos ícones: azul para os agentes, verde para a equipe. */
  tone: string;
  items: string[];
}

function RoleCard({ testId, title, icon: Icon, tone, items }: RoleCardProps) {
  const { t } = useTranslation();

  return (
    <section
      data-testid={testId}
      className="flex flex-col rounded-lg border border-[#e8ebf0] bg-white px-5 py-4"
    >
      <h3
        className={cn(
          "font-mono text-[10px] font-semibold uppercase tracking-[0.14em]",
          tone,
        )}
      >
        {title}
      </h3>

      <ul className="mb-3 mt-3 flex flex-1 flex-col gap-2 text-[12.5px] leading-normal text-[#08254c]">
        {items.map((item) => (
          <li key={item} className="flex gap-2">
            <Icon
              aria-hidden
              className={cn("mt-px h-[15px] w-[15px] shrink-0", tone)}
            />
            <span>{item}</span>
          </li>
        ))}
      </ul>

      <p className="border-t border-[#f2f4f7] pt-2 font-mono text-[10px] tracking-[0.04em] text-[#7b8799]">
        {t(
          "Exed methodology. Not measured data: it describes how the work is split, not what this run found.",
        )}
      </p>
    </section>
  );
}
